import { createRng } from '../rng/seededRandom';
import { aimSlip, anglesTo, hitAngle, leadPoint, steerProjectile, turnToward } from './aiAim';
import { choose, type Plan, type Situation } from './aiBehaviours';
import type { Dir2, PathMemory } from './aiNavigation';
import { perceive, type PerceptionMemory } from './aiPerception';
import { DEFAULT_ARENA_SKILL, type ArenaAiSkill, type SkillProfile } from './aiSkill';
import { characterAiProfile } from './characterAiProfile';
import { humanFriendlyProfile } from './humanFriendlyAi';
import { BURN_REIGNITE } from './burn';
import { contactReach } from './aiContact';
import type { BattleContext } from './battle';
import {
  BURST_SELF_DAMAGE,
  MELEE_ARC,
  WIND_UP,
  WIND_UP_LUNGE,
  distance,
  eyePosition,
  normalizeAngle,
  opponentOf,
  sightBlocked,
} from './combat';
import {
  fighterOf,
  fightsAs,
  type BattleState,
  type FighterId,
  type FighterInput,
  type Vec3,
} from './types';

export { findPath } from './aiNavigation';

/** Live Arena only: simulations, Card, Roll and Watch keep the unchanged calibrated AI. */
export function arenaOpponentInput(
  ctx: BattleContext,
  state: BattleState,
  self: FighterId,
  memory: BattleAiMemory,
  dt: number,
  skill: ArenaAiSkill = DEFAULT_ARENA_SKILL,
  facingHuman = false,
  enabled = true,
): FighterInput {
  if (!enabled || !facingHuman) return battleAiInput(ctx, state, self, memory, dt, skill);
  const profile = characterAiProfile(ctx.game.roster[fightsAs(fighterOf(state, self))]!, skill);
  return {
    ...battleAiInput(ctx, state, self, memory, dt, skill, humanFriendlyProfile(profile)),
    // Applied after the existing pace clamp, including Novice walking and dashes.
    humanFriendlyAi: true,
  };
}

/** Per-fighter scratch state the AI keeps between ticks. */
export interface BattleAiMemory extends PathMemory, PerceptionMemory {
  rng: number;
  /** The current aiming slip, resampled every reaction interval so the aim drifts rather than jitters. */
  slip: { yaw: number; pitch: number; until: number };
  strafeDir: 1 | -1;
  strafeTimer: number;
  rangeWobble: number;
  moveDir: Dir2 | null;
  lastPos: Vec3;
  stuckTime: number;
  /** Battle time the next attack may be released, or -1 while the weapon is not ready. */
  fireAt: number;
  dodge: { dir: Dir2; until: number } | null;
  /** The cover spot last looked for (null when there was none) and when to look again. */
  cover: { spot: Vec3 | null; until: number } | null;
}

export function createBattleAiMemory(seed: number): BattleAiMemory {
  return {
    rng: createRng(seed).state(),
    history: [],
    noticed: new Map(),
    lastEnemyShotId: 0,
    enemyFiredAt: -Infinity,
    slip: { yaw: 0, pitch: 0, until: 0 },
    path: [],
    pathAge: 99,
    pathGoal: null,
    strafeDir: 1,
    strafeTimer: 0,
    rangeWobble: 0,
    moveDir: null,
    lastPos: { x: 0, y: 0, z: 0 },
    stuckTime: 0,
    fireAt: -1,
    dodge: null,
    cover: null,
  };
}

/**
 * The arena AI for one fighter, one fixed step: perceive (a delayed view of the enemy and the
 * shots in the air, never hidden state), choose a behaviour, and turn it into an input at the
 * skill's turn rate, running speed and attack cadence. Deterministic: all chance comes from the
 * memory's seeded generator.
 */
export function battleAiInput(
  ctx: BattleContext,
  state: BattleState,
  self: FighterId,
  memory: BattleAiMemory,
  dt: number,
  skill: ArenaAiSkill = DEFAULT_ARENA_SKILL,
  /** Experiments can inject a profile; gameplay resolves the saved per-game factors. */
  profile: SkillProfile = characterAiProfile(
    ctx.game.roster[fightsAs(fighterOf(state, self))]!,
    skill,
  ),
): FighterInput {
  const me = fighterOf(state, self);
  const enemy = fighterOf(state, opponentOf(state, self));
  if (!me.alive || !enemy.alive)
    return { moveX: 0, moveZ: 0, yaw: me.yaw, pitch: me.pitch, dash: false, attack: false };

  const rng = createRng(memory.rng);
  const view = perceive(ctx.map, state, me, enemy, memory, profile, rng);
  const hp = me.hp / me.maxHp;
  const s: Situation = {
    map: ctx.map,
    state,
    me,
    enemy,
    view,
    memory,
    profile,
    rng,
    dt,
    ranged: me.attackType === 'projectile',
    enemyRanged: enemy.attackType === 'projectile',
    careful: hp < profile.carefulBelow && hp < view.enemyHp / enemy.maxHp,
  };

  memory.strafeTimer -= dt;
  if (memory.strafeTimer <= 0) {
    memory.strafeDir = rng.next() < 0.5 ? 1 : -1;
    memory.strafeTimer = 0.6 + rng.next() * 0.9;
    memory.rangeWobble = (rng.next() * 2 - 1) * profile.rangeWobble;
  }

  const input = act(s, choose(s));
  memory.rng = rng.state();
  return input;
}

/** Turns a plan into this tick's input: aim (with lead and slip), turn, move, dash, attack. */
function act(s: Situation, plan: Plan): FighterInput {
  const { me, view, memory, profile, rng, dt, state } = s;
  const eye = eyePosition(me);
  const aimAt = s.ranged
    ? leadPoint(eye, view.enemyCentre, view.enemyVel, profile.lead)
    : view.enemyCentre;
  if (state.time >= memory.slip.until) {
    memory.slip = {
      yaw: aimSlip(rng, profile.aimSpread),
      pitch: aimSlip(rng, profile.aimSpread * 0.5),
      until: state.time + Math.max(0.15, profile.reaction),
    };
  }
  const aim = anglesTo(eye, aimAt);
  const aimYaw = aim.yaw + memory.slip.yaw;
  const aimPitch = aim.pitch + memory.slip.pitch;
  const faceEnemy = plan.face === 'enemy' || !plan.move;
  // Point blank, the bearing to the enemy is guesswork: keep the facing rather than spin.
  const pointBlank = Math.hypot(aimAt.x - eye.x, aimAt.z - eye.z) < 0.3;
  const wantYaw =
    !faceEnemy && plan.move ? Math.atan2(plan.move.x, plan.move.z) : pointBlank ? me.yaw : aimYaw;
  const yaw = turnToward(me.yaw, wantYaw, profile.turnRate, dt);
  const pitch = turnToward(me.pitch, faceEnemy ? aimPitch : 0, profile.turnRate, dt);

  const input: FighterInput = {
    moveX: 0,
    moveZ: 0,
    speedScale: profile.moveScale,
    yaw,
    pitch,
    dash: false,
    attack: false,
  };
  if (plan.move) {
    // World-space wish direction -> local strafe/forward relative to this tick's facing.
    const k = plan.throttle ?? 1;
    input.moveZ = (plan.move.x * Math.sin(yaw) + plan.move.z * Math.cos(yaw)) * k;
    input.moveX = (plan.move.x * Math.cos(yaw) - plan.move.z * Math.sin(yaw)) * k;
  }
  input.dash = wantsDash(s, plan);
  input.attack = plan.attack && readyToAttack(s, yaw, pitch, aimYaw, aimPitch);
  if (me.steerableProjectile && view.los) {
    // One stick controls all owned shots, just as for a player. Follow the newest
    // visible shot rather than giving each projectile independent homing controls.
    const shot = [...state.projectiles].reverse().find((p) => p.owner === me.id && p.steerable);
    if (shot && !sightBlocked(s.map, eye, shot.pos))
      input.steer = steerProjectile(
        shot,
        view.enemyCentre,
        view.enemyVel,
        profile.lead,
        memory.slip.yaw,
        Math.max(dt, profile.reaction),
      );
  }
  memory.moveDir = plan.move;
  return input;
}

/**
 * Dash when pushing against something without getting anywhere — the burst is usually enough to
 * clear whatever corner the fighter is caught on — and to close the last stretch on a runner.
 */
function wantsDash(s: Situation, plan: Plan): boolean {
  const { me, memory, view, dt } = s;
  if (me.dashCooldown > 0) return false;
  const moved = distance(memory.lastPos, me.pos);
  memory.stuckTime = plan.move && moved < 0.02 ? memory.stuckTime + dt : 0;
  memory.lastPos = { ...me.pos };
  if (memory.stuckTime > 0.4) {
    memory.stuckTime = 0;
    return true;
  }
  // Chasing something just out of reach: spend the dash closing the gap.
  if (s.ranged || !plan.move || !view.los || view.dist <= me.range || view.dist >= me.range + 4)
    return false;
  // A gap-closing dash must point towards the opponent, not along a dodge/orbit
  // or backwards. Otherwise contact fighters dash out of their own attack range.
  const closing =
    (plan.move.x * (view.enemyPos.x - me.pos.x) + plan.move.z * (view.enemyPos.z - me.pos.z)) /
    Math.max(view.dist, 0.001);
  return closing > 0.5;
}

/**
 * Where the believed enemy will be, from where this fighter will be, when a wound-up attack pressed
 * now lands: both carried on as they move for `WIND_UP` (D-42).
 */
function landing(s: Situation): { dist: number; bearing: number } {
  const { me, view } = s;
  const x = view.enemyPos.x + view.enemyVel.x * WIND_UP - (me.pos.x + me.vel.x * WIND_UP);
  const z = view.enemyPos.z + view.enemyVel.z * WIND_UP - (me.pos.z + me.vel.z * WIND_UP);
  return { dist: Math.hypot(x, z), bearing: Math.atan2(x, z) };
}

/**
 * Whether to release the attack now: the weapon is ready, the skill's hesitation has passed and
 * the aim is on the (believed) target. A burst that would kill its owner without killing the
 * enemy is held back.
 */
function readyToAttack(
  s: Situation,
  yaw: number,
  pitch: number,
  aimYaw: number,
  aimPitch: number,
): boolean {
  const { me, view, memory, profile, rng, state } = s;
  if (me.cooldown > 0) {
    memory.fireAt = -1;
    return false;
  }
  // A ring is held, not let off, so the hesitation between blows has nothing to hesitate between:
  // applied to it, the AI pulses the ring instead of holding it and burns nobody.
  if (me.attackType !== 'burn') {
    if (memory.fireAt < 0) memory.fireAt = state.time + profile.cadence * (0.5 + rng.next());
    if (state.time < memory.fireAt) return false;
  }
  const yawOff = Math.abs(normalizeAngle(yaw - aimYaw));
  let lined: boolean;
  switch (me.attackType) {
    case 'projectile':
      lined =
        view.los &&
        yawOff <= hitAngle(s.enemy.halfWidth, view.dist) * profile.fireTolerance &&
        Math.abs(pitch - aimPitch) <=
          hitAngle(s.enemy.height / 2, view.dist) * profile.fireTolerance;
      break;
    case 'aura':
      // A wall stops an aura as it stops a blade; wound up, it must still reach when it lands.
      lined = view.los && landing(s).dist <= me.range + WIND_UP_LUNGE;
      break;
    case 'burn':
      // Nothing to line up: the ring burns whatever stands in it. Raise it when the enemy is
      // inside and there is enough in the account to be worth the raising, and let it fall
      // otherwise rather than paying for empty ground.
      lined = view.los && view.dist <= me.range && me.burn >= (me.burning ? 0 : BURN_REIGNITE);
      break;
    case 'burst': {
      const selfCost = me.maxHp * BURST_SELF_DAMAGE;
      // Dodge/safe-zone plans may permit attacking even while outside contact range.
      // Never spend health bursting at empty ground merely because such a plan permits fire.
      lined =
        view.los &&
        landing(s).dist <=
          contactReach(me.range, profile.reaction, profile.anticipation, view.enemyVel) +
            WIND_UP_LUNGE &&
        (me.hp > selfCost || view.enemyHp <= me.damage);
      break;
    }
    default: {
      // A blow lands a wind-up later (D-42), a lunge further than its range: press it when the
      // enemy will then be inside the swing, not merely when it is lined up at the moment.
      const at = landing(s);
      lined =
        view.los &&
        yawOff <= MELEE_ARC / 2 - 0.12 &&
        at.dist <= me.range + s.enemy.halfWidth + WIND_UP_LUNGE &&
        Math.abs(normalizeAngle(at.bearing - yaw)) <= MELEE_ARC / 2 - 0.12;
    }
  }
  if (lined) memory.fireAt = -1;
  return lined;
}
